import React, { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { Paper, Box } from '@mui/material';
import {
  formatRupeesIndianCompact,
  premiumTooltip,
  horizontalGrid,
  indianValueAxis,
  indianCategoryAxis,
  SLOT_COLORS,
  defaultAnimation,
} from './echartsConfig';
import { reportChartPaperSx } from './reportChartLayout';
import ReportCardHeader, { ReportInsightBanner } from './ReportCardHeader';
import ReportKpiStrip, { kpiMapToItems } from './ReportKpiStrip';

const toNum = v => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const norm = s => String(s || '').toLowerCase().trim();

const FALLBACK_COLORS = [
  '#0891b2', '#8b5cf6', '#059669', '#ca8a04', '#2563eb',
  '#c026d3', '#65a30d', '#ea580c', '#0284c7', '#dc2626',
];

const SLOT_ORDER = [
  'breakfast',
  'morning conference',
  'lunch',
  'hi-tea',
  'hi tea',
  'hightea',
  'high tea',
  'evening',
  'dinner',
  'full day',
  'fullday',
  'overnight',
];

function getSlotColor(label, index) {
  const key = norm(label);
  for (const [slot, color] of Object.entries(SLOT_COLORS)) {
    if (key.includes(norm(slot))) return color;
  }
  return FALLBACK_COLORS[index % FALLBACK_COLORS.length];
}

function getSlotSortKey(label) {
  const key = norm(label);
  for (let i = 0; i < SLOT_ORDER.length; i++) {
    if (key.includes(SLOT_ORDER[i])) return i;
  }
  return SLOT_ORDER.length;
}

function prepareTimeApcDisplay(data) {
  const rawLabels = (data?.labels || []).map(l => String(l ?? ''));
  const rawDatasets = data?.datasets || [];

  const confIdx = rawDatasets.findIndex(ds => norm(ds.label).includes('confirm'));
  const confDs = confIdx >= 0 ? rawDatasets[confIdx] : rawDatasets[0];
  if (!confDs) {
    return { labels: [], values: [], colors: [], highestSlot: '—', lowestSlot: '—', overallApc: 0 };
  }

  const confData = (confDs.data || []).map(toNum);
  const apiColors = Array.isArray(confDs.backgroundColor) ? confDs.backgroundColor : [];
  const useApiOrder = apiColors.length > 0;

  const rows = rawLabels
    .map((label, i) => ({
      label,
      value: confData[i] || 0,
      color: apiColors[i] || null,
    }))
    .filter(r => r.value > 0);

  if (!useApiOrder) {
    rows.sort((a, b) => getSlotSortKey(a.label) - getSlotSortKey(b.label));
  }

  if (!rows.length) {
    return { labels: [], values: [], colors: [], highestSlot: '—', lowestSlot: '—', overallApc: 0 };
  }

  const labels = rows.map(r => r.label);
  const values = rows.map(r => r.value);
  const colors = rows.map((r, i) => r.color || getSlotColor(r.label, i));
  const total = values.reduce((s, v) => s + v, 0);
  const overallApc = values.length > 0 ? total / values.length : 0;

  let highIdx = 0;
  let lowIdx = 0;
  for (let i = 1; i < values.length; i++) {
    if (values[i] > values[highIdx]) highIdx = i;
    if (values[i] < values[lowIdx]) lowIdx = i;
  }

  return {
    labels,
    values,
    colors,
    highestSlot: labels[highIdx],
    highestValue: values[highIdx],
    lowestSlot: labels[lowIdx],
    lowestValue: values[lowIdx],
    overallApc,
  };
}

const TimeApcChart = ({ data, name, handleExport, showButton = true }) => {
  const displayName = data?.name || name;

  const prepared = useMemo(() => prepareTimeApcDisplay(data), [data]);

  const plotHeight = useMemo(
    () => Math.min(440, Math.max(200, prepared.labels.length * 32)),
    [prepared.labels.length],
  );

  const option = useMemo(() => ({
    grid: horizontalGrid,
    tooltip: {
      ...premiumTooltip,
      formatter(params) {
        const p = Array.isArray(params) ? params[0] : params;
        if (!p) return '';
        return `<div style="font-weight:600;font-size:13px;color:#f8fafc;margin-bottom:4px">${p.name}</div>`
          + `<div style="font-variant-numeric:tabular-nums">APC: ${formatRupeesIndianCompact(p.value)}</div>`;
      },
    },
    xAxis: {
      ...indianValueAxis,
      position: 'bottom',
    },
    yAxis: {
      ...indianCategoryAxis,
      data: prepared.labels,
      inverse: true,
    },
    series: [
      {
        type: 'bar',
        data: prepared.values.map((v, i) => ({
          value: v,
          itemStyle: {
            color: prepared.colors[i],
            borderRadius: [0, 3, 3, 0],
          },
        })),
        barWidth: '55%',
        emphasis: { itemStyle: { shadowBlur: 6, shadowColor: 'rgba(0,0,0,0.15)' } },
      },
    ],
    ...defaultAnimation,
  }), [prepared]);

  const apiKpis = kpiMapToItems(data?.kpiMap);
  const infoTooltip = data?.infoText || 'Confirmed avg revenue per cover by time slot — chronological order';

  return (
    <Paper sx={reportChartPaperSx}>
      <ReportCardHeader
        title={displayName}
        insight={infoTooltip}
        handleExport={handleExport}
        showButton={showButton}
      />

      <ReportKpiStrip
        columns={2}
        items={apiKpis || [
          { label: 'Highest APC Slot', value: `${prepared.highestSlot} (${formatRupeesIndianCompact(prepared.highestValue || 0)})` },
          { label: 'Overall APC', value: formatRupeesIndianCompact(prepared.overallApc) },
        ]}
      />

      <ReportInsightBanner text={data?.insightText} style={data?.insightStyle} />

      <Box sx={{ width: '100%', height: plotHeight }}>
        <ReactECharts option={option} style={{ height: '100%', width: '100%' }} notMerge />
      </Box>
    </Paper>
  );
};

export default TimeApcChart;
